import React, { createContext, useContext, useState, useEffect } from 'react';

export interface SelectedOption {
  optionName: string;
  choiceNames: string[];
  priceModifier: number; // total modifier for this option
}

export interface CartItem {
  productId: number;
  name: string;
  price: number; // base price + option modifiers
  basePrice: number; // original product price
  quantity: number;
  image?: string;
  condition?: string;
  selectedOptions?: SelectedOption[];
  /** Unique key for cart deduplication — productId for simple, productId + options hash for configurable */
  cartKey: string;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity' | 'cartKey'>, quantity?: number) => void;
  removeFromCart: (cartKey: string) => void;
  updateQuantity: (cartKey: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

/** Generate a stable cart key from product + selected options */
function makeCartKey(productId: number, selectedOptions?: SelectedOption[]): string {
  if (!selectedOptions || selectedOptions.length === 0) return String(productId);
  const optStr = selectedOptions
    .map(o => `${o.optionName}:${o.choiceNames.sort().join(',')}`)
    .sort()
    .join('|');
  return `${productId}__${optStr}`;
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('shopCart');
    if (!saved) return [];
    try {
      const parsed = JSON.parse(saved) as any[];
      // Migration: add cartKey and basePrice if missing
      return parsed.map(item => ({
        ...item,
        basePrice: item.basePrice ?? item.price,
        cartKey: item.cartKey ?? String(item.productId),
      }));
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('shopCart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item: Omit<CartItem, 'quantity' | 'cartKey'>, quantity: number = 1) => {
    const cartKey = makeCartKey(item.productId, item.selectedOptions);
    setCartItems(prev => {
      const existing = prev.find(i => i.cartKey === cartKey);
      if (existing) {
        return prev.map(i => i.cartKey === cartKey ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, { ...item, quantity, cartKey }];
    });
  };

  const removeFromCart = (cartKey: string) => {
    setCartItems(prev => prev.filter(item => item.cartKey !== cartKey));
  };

  const updateQuantity = (cartKey: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCartItems(prev => prev.map(item => item.cartKey === cartKey ? { ...item, quantity } : item));
  };

  const clearCart = () => setCartItems([]);

  const getTotalItems = () => cartItems.reduce((t, i) => t + i.quantity, 0);

  const getTotalPrice = () => cartItems.reduce((t, i) => t + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart, getTotalItems, getTotalPrice }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
